import { CODE_COVER_COLS, CODE_COVER_ROWS, decodeCodeCover } from "@squeek/module-contracts/community-code-v1";
import type { CommunityAdminProject } from "@squeek/module-contracts/community-admin-v1";
import { useEffect, useRef } from "react";

/** Packed LED levels only: never loads a member-supplied image URL. */
export function ProjectCover({ cover, width = 256 }: { cover: CommunityAdminProject["cover"]; width?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || cover === null) return;
    const levels = decodeCodeCover(cover);
    const draw = () => {
      const size = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(size.width * ratio));
      canvas.height = Math.max(1, Math.round(size.height * ratio));
      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(canvas.width / CODE_COVER_COLS, 0, 0, canvas.height / CODE_COVER_ROWS, 0, 0);
      context.clearRect(0, 0, CODE_COVER_COLS, CODE_COVER_ROWS);
      context.fillStyle = "#080809";
      context.fillRect(0, 0, CODE_COVER_COLS, CODE_COVER_ROWS);
      for (let index = 0; index < levels.length; index++) {
        context.fillStyle = `rgba(242,242,244,${0.05 + (levels[index]! / 4) * 0.95})`;
        context.beginPath();
        context.arc(index % CODE_COVER_COLS + 0.5, Math.floor(index / CODE_COVER_COLS) + 0.5, 0.38, 0, Math.PI * 2);
        context.fill();
      }
    };
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    window.addEventListener("resize", draw);
    draw();
    return () => { observer.disconnect(); window.removeEventListener("resize", draw); };
  }, [cover]);
  const style = { width, maxWidth: "100%", aspectRatio: `${CODE_COVER_COLS} / ${CODE_COVER_ROWS}`, display: "block", borderRadius: 6 };
  return cover === null
    ? <span style={{ ...style, display: "grid", placeItems: "center", background: "var(--c-surface-2)", color: "var(--c-fg-3)", fontSize: 13 }}>No cover</span>
    : <canvas ref={ref} style={style} role="img" aria-label="Submitted project cover" />;
}
